import { formatFeeAdjustmentAmount } from "../../fee-adjustment";
import { APPROVAL_BUSINESS_LINKS } from "../config";
import type { ApprovalRoundLog, ApprovalRoundStatus, ApprovalRoundStep, ApprovalStepResult } from "../types";

const statusLabels: Record<ApprovalRoundStatus, string> = {
  pending: "审批中",
  approved: "已通过",
  rejected: "已驳回",
  withdrawn: "已撤回",
};

const resultLabels: Record<ApprovalStepResult, string> = { approved: "通过", rejected: "驳回" };

/** 一轮审批的状态名。 */
export function formatRoundStatus(status: ApprovalRoundStatus): string {
  return statusLabels[status];
}

/** 一级的处理结果名。 */
export function formatStepResult(result: ApprovalStepResult): string {
  return resultLabels[result];
}

/** 金额显示为 ¥ 加千分位两位小数，全程不经过浮点数。 */
export function formatApprovalAmount(amount: string): string {
  return formatFeeAdjustmentAmount(amount, false);
}

/** 第几级共几级。 */
export function formatRoundProgress(currentStepNo: number, stepCount: number): string {
  return `第 ${currentStepNo} 级 / 共 ${stepCount} 级`;
}

/** 后端北京时间显示到秒；为空显示「—」。 */
export function formatApprovalTime(value: string | null): string {
  return value ? value.replace("T", " ").slice(0, 19) : "—";
}

/** 一级的审批人；所选部门的审批人这一级注明来源。 */
export function formatStepApprovers(step: ApprovalRoundStep): string {
  const names = step.approvers.map((user) => user.name).join("、");
  return step.approverType === "dept_approver" ? `${names}（所选部门的审批人）` : names;
}

/** 一级现在怎样：处理过的写谁、怎么处理、什么时候；没处理的按所在那一轮的进度写审批中、未到这一级或未处理。 */
export function describeRoundStep(round: ApprovalRoundLog, step: ApprovalRoundStep): string {
  if (step.result) return `${step.handledByName ?? "—"} ${resultLabels[step.result]} · ${formatApprovalTime(step.handledAt)}`;
  if (round.status !== "pending") return "未处理";
  if (step.stepNo === round.currentStepNo) return "审批中";
  return step.stepNo > round.currentStepNo ? "未到这一级" : "未处理";
}

/** 驳回必须填意见；去掉空白后为空即不合规。 */
export function validateRejectRemark(remark: string): string {
  return remark.trim() ? "" : "驳回要填写意见。";
}

/** 详情里打开业务单的链接（文字和地址）；这种审批类型没配业务单地址时为 null，不出链接。 */
export function resolveApprovalBusinessLink(typeCode: string, businessId: string): { label: string; href: string } | null {
  const link = APPROVAL_BUSINESS_LINKS.get(typeCode);
  return link ? { label: link.label, href: link.path(businessId) } : null;
}
